<script setup>
import { ref, onMounted } from "vue"
import { useRoute } from "vue-router"
import Layout from "../../layouts/main.vue"
import PageHeader from "@/components/page-header"

import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"

const title = "جزئیات فاکتور"
const items = [
  {
    text: "سفارشات",
    href: "/",
  },
  {
    text: "جزئیات فاکتور",
    active: true,
  },
]

const address = ref(
  "تهران، ستارخان - بازارچه سنتی - فاز یک - طبقه اول - واحد ۱۲۷/۱"
)
const contact = ref("021-44443933")
const email = ref("support@yaadino.com")
const website = ref("www.yaadino.com")

const route = useRoute()
const id = route.params.id

const orderDetails = ref([])
const userInfo = ref()

const transactionStatusLabels = {
  STATUS_FAILED: "تراکنش ناموفق",
  STATUS_PENDING: "در حال انجام",
  STATUS_SUCCESS: "پرداخت شده",
}

onMounted(async () => {
  const { data } = await store.dispatch("getOrder", id)
  orderDetails.value = {
    ...data.data,
  }

  const userData = await store.dispatch("getUser", orderDetails.value.user_id)
  userInfo.value = {
    ...userData.data.data,
  }
})

const parseDiscountDetails = (discountDetails) => {
  const discountObject = JSON.parse(discountDetails)

  let html = ""
  html +=
    "<strong>مقدار:</strong> " +
    formattedPrices(discountObject.amount) +
    "<br />"
  html +=
    "<strong>قیمت با تخفیف:</strong> " +
    formattedPrices(discountObject.priceWithDiscount)
  return html
}

const formattedPrices = (value) => {
  return new Intl.NumberFormat("fa-IR").format(value)
}

const toPersianDate = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD")
}

const toPersianTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("HH:mm:ss")
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-row class="justify-content-center">
      <b-col xxl="9">
        <b-card no-body id="demo">
          <b-card-header class="border-bottom-dashed p-4">
            <div class="d-sm-flex">
              <div class="flex-grow-1">
                <img
                  src="@/assets/images/invoice-logo.png"
                  class="card-logo card-logo-dark"
                  alt="logo dark"
                  height="34"
                />
                <img
                  src="@/assets/images/invoice-logo.png"
                  class="card-logo card-logo-light"
                  alt="logo light"
                  height="34"
                />
                <div class="mt-sm-5 mt-3">
                  <h6 class="text-muted text-uppercase fw-semibold">آدرس</h6>
                  <p class="text-muted mb-1" id="address-details">
                    {{ address }}
                  </p>
                  <p class="text-muted mb-0" id="zip-code">
                    <span>کد پستی:</span>
                    {{ persianTools.digitsEnToFa(1122334455) }}
                  </p>
                </div>
              </div>
              <div class="flex-shrink-0 mt-sm-0 mt-3">
                <h6>
                  <span class="text-muted fw-normal">ایمیل: </span>
                  <span id="email">{{ email }}</span>
                </h6>
                <h6>
                  <span class="text-muted fw-normal">وب سایت: </span>
                  <b-link
                    href="#"
                    class="link-primary"
                    target="_blank"
                    id="website"
                    >{{ website }}</b-link
                  >
                </h6>
                <h6 class="mb-0">
                  <span class="text-muted fw-normal">شماره تماس: </span
                  ><span id="contact-no" dir="ltr">{{
                    persianTools.digitsEnToFa(contact)
                  }}</span>
                </h6>
              </div>
            </div>
          </b-card-header>
          <b-card-body class="p-4">
            <b-row class="g-3">
              <b-col lg="3" cols="6">
                <p class="text-muted mb-2 text-uppercase fw-semibold">
                  شماره فاکتور
                </p>
                <h5 class="fs-14 mb-0">
                  <span id="invoice-no">
                    {{
                      orderDetails.trackingCode
                        ? persianTools.digitsEnToFa(orderDetails.trackingCode)
                        : orderDetails.trackingCode
                    }}</span
                  >
                </h5>
              </b-col>
              <b-col lg="3" cols="6">
                <p class="text-muted mb-2 text-uppercase fw-semibold">تاریخ</p>
                <h5 class="fs-14 mb-0">
                  <span id="invoice-date" class="ms-2">{{
                    orderDetails.dateTime
                      ? toPersianDate(orderDetails.dateTime)
                      : ""
                  }}</span>
                  <small class="text-muted" id="invoice-time">{{
                    orderDetails.dateTime
                      ? toPersianTime(orderDetails.dateTime)
                      : ""
                  }}</small>
                </h5>
              </b-col>
              <b-col lg="3" cols="6">
                <p class="text-muted mb-2 text-uppercase fw-semibold">
                  وضعیت پرداخت
                </p>
                <span class="fs-11" id="payment-status">{{
                  orderDetails.transactionInfo
                    ? transactionStatusLabels[
                        orderDetails.transactionInfo.status
                      ]
                    : ""
                }}</span>
              </b-col>
              <b-col lg="3" cols="6">
                <p class="text-muted mb-2 text-uppercase fw-semibold">
                  مبلغ کل
                </p>
                <h5 class="fs-14 mb-0">
                  <span id="total-amount">{{
                    formattedPrices(orderDetails.totalPrice)
                  }}</span>
                </h5>
              </b-col>
            </b-row>
          </b-card-body>
          <b-card-body class="p-4 border-top border-top-dashed">
            <b-row class="g-3">
              <b-col sm="12">
                <h6 class="text-muted text-uppercase fw-semibold mb-3">
                  اطلاعات مشتری
                </h6>
                <p class="fw-medium mb-2" id="billing-name">
                  {{ userInfo ? userInfo.name + " " + userInfo.surname : "" }}
                </p>
                <p class="text-muted mb-1" id="billing-address">
                  {{ orderDetails ? orderDetails.deliveryAddress : "" }}
                </p>
                <p class="text-muted mb-1" id="billing-phone">
                  شماره تماس:
                  {{
                    userInfo ? persianTools.digitsEnToFa(userInfo.phone) : ""
                  }}
                </p>
              </b-col>
            </b-row>
          </b-card-body>
          <b-card-body class="p-4">
            <div class="table-responsive">
              <table
                class="table table-borderless text-center table-nowrap align-middle mb-0"
              >
                <thead>
                  <tr class="table-active">
                    <th scope="col" style="width: 50px">#</th>
                    <th scope="col">محصولات</th>
                    <th scope="col">قیمت هر محصول</th>
                    <th scope="col">تخفیف</th>
                    <th scope="col">تعداد</th>
                    <th scope="col" class="text-end">قیمت نهایی</th>
                  </tr>
                </thead>
                <tbody id="products-list">
                  <tr v-for="(item, index) of orderDetails.items" :key="index">
                    <th scope="row">{{ index + 1 }}</th>
                    <td class="text-start">
                      <span class="fw-medium">{{ item.product.name }}</span>
                      <p
                        v-for="(value, index) of item.product.values"
                        :key="index"
                        class="text-muted mb-0"
                      >
                        {{ value.attributeName }}:
                        <span class="fw-medium">{{ value.valueName }}</span>
                      </p>
                      <p
                        v-if="item.product.createdBy"
                        class="text-muted mb-0 mt-1"
                      >
                        <strong>فروشگاه: </strong>
                        <span class="fw-medium">{{
                          item.product.createdBy
                        }}</span>
                      </p>
                    </td>
                    <td>{{ formattedPrices(item.originalPrice) }}</td>
                    <td class="text-start">
                      <div v-if="item.discountDetails">
                        <p
                          v-if="typeof item.discountDetails === 'string'"
                          v-html="parseDiscountDetails(item.discountDetails)"
                        ></p>
                        <p v-else>
                          <strong>مقدار تخفیف:</strong>
                          {{ item.discountDetails.amount }}<br />
                          <strong>قیمت با تخفیف:</strong>
                          {{ item.discountDetails.priceWithDiscount }}
                        </p>
                      </div>

                      <p v-else>تخفیف موجود نیست.</p>
                    </td>
                    <td>{{ persianTools.digitsEnToFa(item.qty) }}</td>
                    <td class="text-end">{{ formattedPrices(item.price) }}</td>
                  </tr>
                  <tr class="border-top border-top-dashed mt-2">
                    <td colspan="3"></td>
                    <td colspan="3" class="fw-medium p-0">
                      <table
                        class="table table-borderless text-start table-nowrap align-middle mb-0"
                      >
                        <tbody>
                          <tr>
                            <td>جمع جزء :</td>
                            <td class="text-end">
                              {{ formattedPrices(orderDetails.subTotal) }}
                            </td>
                          </tr>
                          <tr>
                            <td>هزینه ارسال پست :</td>
                            <td class="text-end">
                              {{ formattedPrices(orderDetails.shippingCost) }}
                            </td>
                          </tr>
                          <tr>
                            <td>هزینه مالیات :</td>
                            <td class="text-end">
                              {{ formattedPrices(orderDetails.taxAmount) }}
                            </td>
                          </tr>
                          <tr class="border-top border-top-dashed">
                            <th scope="row">جمع کل (تومان) :</th>
                            <th class="text-end">
                              {{ formattedPrices(orderDetails.totalPrice) }}
                            </th>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="hstack gap-2 justify-content-end d-print-none mt-4">
              <b-link href="javascript:window.print()" class="btn btn-success"
                ><i class="ri-printer-line align-bottom me-1"></i> پرینت</b-link
              >
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>
